import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import AppRouter from './routes/AppRouter';
import { useDispatch } from 'react-redux';
import { observeUser } from './features/auth/authSlice';
import ReleaseNoticeModal from './components/common/ReleaseNoticeModal';
import { releaseNotice } from './data/releaseNotice';
import { initUploadCounter } from './utils/uploadCounter';
import { initDailyCounterSystem } from './utils/dailyPolicyCounter';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/mobile.css';

// Create a custom theme that completely disables responsive typography
const theme = createTheme({
  palette: {
    primary: {
      main: '#0051C3',
    },
  },
  typography: {
    fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    // Completely disable responsive typography and force consistent sizing
    h1: {
      fontSize: '2.5rem !important',
      lineHeight: 1.2,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '2.5rem !important' },
      '@media (min-width:600px)': { fontSize: '2.5rem !important' },
      '@media (min-width:900px)': { fontSize: '2.5rem !important' },
      '@media (min-width:1200px)': { fontSize: '2.5rem !important' },
      '@media (min-width:1536px)': { fontSize: '2.5rem !important' },
    },
    h2: {
      fontSize: '2rem !important',
      lineHeight: 1.2,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '2rem !important' },
      '@media (min-width:600px)': { fontSize: '2rem !important' },
      '@media (min-width:900px)': { fontSize: '2rem !important' },
      '@media (min-width:1200px)': { fontSize: '2rem !important' },
      '@media (min-width:1536px)': { fontSize: '2rem !important' },
    },
    h3: {
      fontSize: '1.75rem !important',
      lineHeight: 1.2,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '1.75rem !important' },
      '@media (min-width:600px)': { fontSize: '1.75rem !important' },
      '@media (min-width:900px)': { fontSize: '1.75rem !important' },
      '@media (min-width:1200px)': { fontSize: '1.75rem !important' },
      '@media (min-width:1536px)': { fontSize: '1.75rem !important' },
    },
    h4: {
      fontSize: '1.5rem !important',
      lineHeight: 1.2,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '1.5rem !important' },
      '@media (min-width:600px)': { fontSize: '1.5rem !important' },
      '@media (min-width:900px)': { fontSize: '1.5rem !important' },
      '@media (min-width:1200px)': { fontSize: '1.5rem !important' },
      '@media (min-width:1536px)': { fontSize: '1.5rem !important' },
    },
    h5: {
      // Special styling for headers - allow Roboto Slab to be used
      fontSize: '32px !important',
      lineHeight: '56px',
      fontWeight: 500,
      fontFamily: 'Roboto Slab, serif',
      letterSpacing: '0.02em',
      '@media (min-width:0px)': { fontSize: '32px !important' },
      '@media (min-width:600px)': { fontSize: '32px !important' },
      '@media (min-width:900px)': { fontSize: '32px !important' },
      '@media (min-width:1200px)': { fontSize: '32px !important' },
      '@media (min-width:1536px)': { fontSize: '32px !important' },
    },
    h6: {
      fontSize: '1rem !important',
      lineHeight: 1.2,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '1rem !important' },
      '@media (min-width:600px)': { fontSize: '1rem !important' },
      '@media (min-width:900px)': { fontSize: '1rem !important' },
      '@media (min-width:1200px)': { fontSize: '1rem !important' },
      '@media (min-width:1536px)': { fontSize: '1rem !important' },
    },
    body1: {
      fontSize: '1rem !important',
      lineHeight: 1.5,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '1rem !important' },
      '@media (min-width:600px)': { fontSize: '1rem !important' },
      '@media (min-width:900px)': { fontSize: '1rem !important' },
      '@media (min-width:1200px)': { fontSize: '1rem !important' },
      '@media (min-width:1536px)': { fontSize: '1rem !important' },
    },
    body2: {
      fontSize: '0.875rem !important',
      lineHeight: 1.43,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '0.875rem !important' },
      '@media (min-width:600px)': { fontSize: '0.875rem !important' },
      '@media (min-width:900px)': { fontSize: '0.875rem !important' },
      '@media (min-width:1200px)': { fontSize: '0.875rem !important' },
      '@media (min-width:1536px)': { fontSize: '0.875rem !important' },
    },
    subtitle1: {
      fontSize: '1rem !important',
      lineHeight: 1.5,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '1rem !important' },
      '@media (min-width:600px)': { fontSize: '1rem !important' },
      '@media (min-width:900px)': { fontSize: '1rem !important' },
      '@media (min-width:1200px)': { fontSize: '1rem !important' },
      '@media (min-width:1536px)': { fontSize: '1rem !important' },
    },
    subtitle2: {
      fontSize: '0.875rem !important',
      lineHeight: 1.43,
      fontWeight: 500,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '0.875rem !important' },
      '@media (min-width:600px)': { fontSize: '0.875rem !important' },
      '@media (min-width:900px)': { fontSize: '0.875rem !important' },
      '@media (min-width:1200px)': { fontSize: '0.875rem !important' },
      '@media (min-width:1536px)': { fontSize: '0.875rem !important' },
    },
    caption: {
      fontSize: '0.75rem !important',
      lineHeight: 1.66,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '0.75rem !important' },
      '@media (min-width:600px)': { fontSize: '0.75rem !important' },
      '@media (min-width:900px)': { fontSize: '0.75rem !important' },
      '@media (min-width:1200px)': { fontSize: '0.75rem !important' },
      '@media (min-width:1536px)': { fontSize: '0.75rem !important' },
    },
    overline: {
      fontSize: '0.75rem !important',
      lineHeight: 2.66,
      fontWeight: 400,
      fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      '@media (min-width:0px)': { fontSize: '0.75rem !important' },
      '@media (min-width:600px)': { fontSize: '0.75rem !important' },
      '@media (min-width:900px)': { fontSize: '0.75rem !important' },
      '@media (min-width:1200px)': { fontSize: '0.75rem !important' },
      '@media (min-width:1536px)': { fontSize: '0.75rem !important' },
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        h5: {
          // Allow Roboto Slab for h5 headers
          fontFamily: 'Roboto Slab, serif',
          fontSize: '32px !important',
          fontWeight: 500,
          lineHeight: '56px',
          letterSpacing: '0.02em',
          '@media (min-width:0px)': { fontSize: '32px !important' },
          '@media (min-width:600px)': { fontSize: '32px !important' },
          '@media (min-width:900px)': { fontSize: '32px !important' },
          '@media (min-width:1200px)': { fontSize: '32px !important' },
          '@media (min-width:1536px)': { fontSize: '32px !important' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '16px !important',
          lineHeight: 1.5,
          fontWeight: 400,
          fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
          '@media (min-width:0px)': { fontSize: '16px !important' },
          '@media (min-width:600px)': { fontSize: '16px !important' },
          '@media (min-width:900px)': { fontSize: '16px !important' },
          '@media (min-width:1200px)': { fontSize: '16px !important' },
          '@media (min-width:1536px)': { fontSize: '16px !important' },
        },
        head: {
          fontSize: '16px !important',
          fontWeight: 500,
          lineHeight: 1.5,
          fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
          '@media (min-width:0px)': { fontSize: '16px !important' },
          '@media (min-width:600px)': { fontSize: '16px !important' },
          '@media (min-width:900px)': { fontSize: '16px !important' },
          '@media (min-width:1200px)': { fontSize: '16px !important' },
          '@media (min-width:1536px)': { fontSize: '16px !important' },
        },
        body: {
          fontSize: '16px !important',
          lineHeight: 1.5,
          fontWeight: 400,
          fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
          '@media (min-width:0px)': { fontSize: '16px !important' },
          '@media (min-width:600px)': { fontSize: '16px !important' },
          '@media (min-width:900px)': { fontSize: '16px !important' },
          '@media (min-width:1200px)': { fontSize: '16px !important' },
          '@media (min-width:1536px)': { fontSize: '16px !important' },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontSize: '16px !important',
            fontWeight: 500,
            lineHeight: 1.5,
            fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
            '@media (min-width:0px)': { fontSize: '16px !important' },
            '@media (min-width:600px)': { fontSize: '16px !important' },
            '@media (min-width:900px)': { fontSize: '16px !important' },
            '@media (min-width:1200px)': { fontSize: '16px !important' },
            '@media (min-width:1536px)': { fontSize: '16px !important' },
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontSize: '16px !important',
            lineHeight: 1.5,
            fontWeight: 400,
            fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
            '@media (min-width:0px)': { fontSize: '16px !important' },
            '@media (min-width:600px)': { fontSize: '16px !important' },
            '@media (min-width:900px)': { fontSize: '16px !important' },
            '@media (min-width:1200px)': { fontSize: '16px !important' },
            '@media (min-width:1536px)': { fontSize: '16px !important' },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem !important',
          fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
          '@media (min-width:0px)': { fontSize: '0.875rem !important' },
          '@media (min-width:600px)': { fontSize: '0.875rem !important' },
          '@media (min-width:900px)': { fontSize: '0.875rem !important' },
          '@media (min-width:1200px)': { fontSize: '0.875rem !important' },
          '@media (min-width:1536px)': { fontSize: '0.875rem !important' },
        },
        containedPrimary: {
          backgroundColor: '#0051C3 !important',
          '&:hover': {
            backgroundColor: '#003d91 !important',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontSize: '16px !important',
            fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
            '@media (min-width:0px)': { fontSize: '16px !important' },
            '@media (min-width:600px)': { fontSize: '16px !important' },
            '@media (min-width:900px)': { fontSize: '16px !important' },
            '@media (min-width:1200px)': { fontSize: '16px !important' },
            '@media (min-width:1536px)': { fontSize: '16px !important' },
          },
          // Fix for notched outline and labels
          '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            backgroundColor: 'transparent',
            padding: '0 4px',
          },
          '& .MuiOutlinedInput-root': {
            '& legend': {
              fontSize: '0.75em', // Adjust as needed to match the shrink label size
            }
          },
          // Specific fix for autofill background
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #e8f0fe inset !important',
            WebkitTextFillColor: '#212121 !important',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          '&.MuiInputLabel-shrink': {
            backgroundColor: 'transparent !important',
          }
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem !important',
          fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
          '@media (min-width:0px)': { fontSize: '0.875rem !important' },
          '@media (min-width:600px)': { fontSize: '0.875rem !important' },
          '@media (min-width:900px)': { fontSize: '0.875rem !important' },
          '@media (min-width:1200px)': { fontSize: '0.875rem !important' },
          '@media (min-width:1536px)': { fontSize: '0.875rem !important' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem !important',
          fontFamily: 'Roboto, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
          '@media (min-width:0px)': { fontSize: '0.75rem !important' },
          '@media (min-width:600px)': { fontSize: '0.75rem !important' },
          '@media (min-width:900px)': { fontSize: '0.75rem !important' },
          '@media (min-width:1200px)': { fontSize: '0.75rem !important' },
          '@media (min-width:1536px)': { fontSize: '0.75rem !important' },
        },
      },
    },
  },
});

export default function App() {
  const dispatch = useDispatch();
  const [showReleaseModal, setShowReleaseModal] = useState(false);

  // Global handler for Google Drive Success/Error Toasts
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const driveLinked = params.get('drive_linked');
    const driveError = params.get('drive_error');
    const source = params.get('source');

    if (driveLinked === 'true') {
      console.log('✅ [App] Drive linked detected!');
      if (source !== 'onboarding') {
        toast.success('Successfully connected Google Drive', {
          containerId: 'bottom-center',
          position: 'bottom-center',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          closeButton: false,
          style: {
            backgroundColor: '#166434',
            color: '#FFFFFF',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 500,
            padding: '12px 16px',
            minHeight: '44px',
            alignItems: 'center',
            gap: '2px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            marginBottom: window.innerWidth <= 1024 ? '75vh' : '20px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: 'auto',
            maxWidth: 'none',
            display: 'inline-flex',
          },
          icon: (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: '#FFFFFF' }}
            >
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
        });
      }

      // Cleanup URL after a delay
      setTimeout(() => {
        const url = new URL(window.location);
        url.searchParams.delete('drive_linked');
        url.searchParams.delete('source');
        window.history.replaceState({}, '', url);
      }, 2000);
    } else if (driveError) {
      console.log('❌ [App] Drive error detected:', driveError);
      toast.error(`Drive Error: ${decodeURIComponent(driveError)}`, {
        containerId: 'top-right',
        position: 'top-right'
      });

      setTimeout(() => {
        const url = new URL(window.location);
        url.searchParams.delete('drive_error');
        window.history.replaceState({}, '', url);
      }, 2000);
    }
  }, []);

  useEffect(() => {
    dispatch(observeUser());

    // Initialize the counter systems with proper cleanup
    try {
      const cleanupUpload = initUploadCounter();
      const cleanupDaily = initDailyCounterSystem();
      console.log('🔄 Counter systems initialized');

      // Return cleanup function
      return () => {
        cleanupUpload();
        cleanupDaily();
        console.log('🔄 Counter systems cleaned up');
      };
    } catch (error) {
      console.error('❌ Failed to initialize counter systems:', error);
    }
  }, [dispatch]);

  // Check if release notice should be shown
  useEffect(() => {
    const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format based on local time
    const lastShownDate = localStorage.getItem('policytrackr_notice_shown_date');
    const releaseDate = releaseNotice.date;

    // Show modal if:
    // 1. No date stored in localStorage, OR
    // 2. The stored date is not today, AND
    // 3. Today's date matches the release date (for specific day targeting)
    if (lastShownDate !== today && today === releaseDate) {
      setShowReleaseModal(true);
    }
  }, []);

  const handleCloseReleaseModal = () => {
    setShowReleaseModal(false);
  };

  useEffect(() => {
    const initMessaging = async () => {
      try {
        const supported = await isSupported();
        if (!supported) {
          console.warn('Firebase Messaging is not supported in this browser or environment (e.g., non-HTTPS context).');
          return;
        }

        const messaging = getMessaging();

        // Request permission and get token
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          const currentToken = await getToken(messaging, {
            vapidKey: 'BDAqVMD_4GUt60P67YUfZ0pkaLzJSHGz8OTgH38XMXWoZTpNqFbrhZ9ROAFlgIXRPDF534hxBNoZoUtVGiDIROI',
          });

          if (currentToken) {
            const auth = getAuth();
            const user = auth.currentUser;

            if (user) {
              const db = getFirestore();
              await setDoc(doc(db, 'users', user.uid, 'tokens', currentToken), {
                createdAt: new Date(),
              });
            }
          }
        }

        // Handle foreground messages
        onMessage(messaging, (payload) => {
          console.log('Message received. ', payload);
        });
      } catch (error) {
        console.error('Error initializing messaging:', error);
      }
    };

    initMessaging();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      {/* Toast Containers moved here to be always active */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        enableMultiContainer={true}
        containerId="top-right"
      />
      <ToastContainer
        position="bottom-center"
        autoClose={2000}
        enableMultiContainer={true}
        containerId="bottom-center"
        toastClassName="bottom-center-toast"
      />

      <AppRouter />
      <ReleaseNoticeModal
        open={showReleaseModal}
        onClose={handleCloseReleaseModal}
      />
    </ThemeProvider>
  );
}
